import clsx from 'clsx'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import SearchLocationInput from 'components/Search/SearchForm/SearchLocationInput/SearchLocationInput'
import {
  CAR_HIRE_SEARCH_TYPES,
} from 'constants/carHire'
import CarHireDatePicker from './CarHireDatePickerDropdown'
import CarHireDateTimeField from './CarHireDateTimeField'
import { SearchMenuStates } from 'components/Search/type'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import { pushWithRegion } from 'actions/NavigationActions'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import {
  ISO_DATE_FORMAT,
  INTL_SHORT_DAY_NAME_DAY_MONTH,
  INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR,
} from 'constants/dateFormats'
import qs from 'qs'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useLocation } from 'react-router-dom'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import CarHireSearchDriversAgeCategorySelect from './CarHireSearchDriversAgeCategorySelect'
import CarHireDriversAgeInput from './CarHireDriversAgeInput'
import { getCarHireListKey, getCarHireSearchId, getCarHireSearchType } from '../carHireUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { trackEvent } from 'analytics/analytics'
import config from 'constants/config'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import {
  applyCarHireSearchRefinements,
  markCarHireSearchRefinementsApplied,
} from '../carHireSearchRefinements'

const ControlsContainer = styled.div`
  display: grid;
  position: relative;
  gap: ${rem(12)};

  grid-template:
    "location"
    "pick-up"
    "drop-off"
    "actions" / 1fr;

  ${mediaQueryUp.tablet} {
    grid-template:
      "location location location"
      "pick-up drop-off actions" auto / 1fr 1fr ${rem(112)};
  }

  ${mediaQueryUp.desktop} {
    grid-template: "location pick-up drop-off actions" auto / 1fr ${rem(260)} ${rem(260)} ${rem(112)};
  }

  > .location-input {
    grid-area: location;
  }
  > .pick-up-input {
    grid-area: pick-up;
  }
  > .drop-off-input {
    grid-area: drop-off;
  }
  > .traveller-input {
    grid-area: traveller;
  }
`

// Dodgy override as this input needed to fit within the existing line height
// but there's no LuxKit compatible component
const DriversAgeInput = styled(CarHireDriversAgeInput)`
  width: ${rem(58)};
  height: ${rem(32)};

  > input {
    padding: ${rem(6)} ${rem(16)};
  }
`

const DriversAgeContainer = styled(Group)`
  opacity: 1;
  transition: opacity 0.2s;

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`

const SearchButton = styled(TextButton)`
  height: 100%;
  grid-area: actions;
`

interface Props {
  className?: string
  /**
   * Will attempt to submit the form and search when a user has finished entering the information
   * without the "search" button being required to be clicked
   */
  optimisticSubmits?: boolean
  offerLists: Record<string, App.CarHireList>
}

function CarHireSearchBar(props: Props) {
  const { optimisticSubmits, className, offerLists } = props
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const {
    searchItem,
    secondarySearchItem,
    checkinDate: pickUpDate,
    checkoutDate: dropOffDate,
    pickUpTime,
    dropOffTime,
    driversAgeCategory,
    driversAge,
    bundledOrderId,
    pickUpLatitude,
    pickUpLongitude,
    dropOffLatitude,
    dropOffLongitude,
  } = useContext(GlobalSearchStateContext)

  const formRef = useRef<HTMLFormElement>(null)
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const dispatch = useAppDispatch()
  const { pathname, search } = useLocation()

  const [differentLocations, setDifferentLocations] = useState<boolean>(!!(secondarySearchItem && searchItem?.format.mainText !== secondarySearchItem?.format.mainText))

  const listKey = useMemo(() => {
    const dropOffItem = differentLocations ? secondarySearchItem : searchItem
    const hasPickUpGeoLocation = pickUpLatitude && pickUpLongitude
    const hasDropOffGeoLocation = dropOffLatitude && dropOffLongitude
    const filters = {
      driversAgeCategory,
      dropOffDate: dropOffDate?.format(ISO_DATE_FORMAT),
      dropOffTime,
      pickUpDate: pickUpDate?.format(ISO_DATE_FORMAT),
      pickUpTime,
      pickUpLocationId: getCarHireSearchId(searchItem),
      dropOffLocationId: getCarHireSearchId(dropOffItem),
      ...((driversAgeCategory === 1 || driversAgeCategory === 3) && {
        driversAge,
      }),
      ...(bundledOrderId && { bundledOrderId }),
      ...(hasPickUpGeoLocation && { pickUpLatitude, pickUpLongitude }),
      ...(hasPickUpGeoLocation && hasDropOffGeoLocation && { dropOffLatitude, dropOffLongitude }),
    }

    return getCarHireListKey(filters)
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const duration = useMemo(() => offerLists[listKey]?.vehicles[0]?.duration, [listKey, offerLists])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Closed })

    const pickUpItem = searchItem ?? ANYWHERE_SEARCH_ITEM
    const dropOffItem = differentLocations ? secondarySearchItem : searchItem

    const hasPickUpGeoLocation = pickUpLatitude && pickUpLongitude
    const hasDropOffGeoLocation = dropOffLatitude && dropOffLongitude

    const currentSearch = pathname.includes('/search/car-hire') ? search : undefined
    const searchQuery = qs.stringify({
      pickUpName: pickUpItem.format.mainText,
      dropOffName: dropOffItem?.format.mainText,
      pickUpId: getCarHireSearchId(pickUpItem),
      dropOffId: getCarHireSearchId(dropOffItem),
      pickUpDate: pickUpDate?.format(ISO_DATE_FORMAT),
      pickUpTime,
      dropOffDate: dropOffDate?.format(ISO_DATE_FORMAT),
      dropOffTime,
      ageCategory: driversAgeCategory,
      age: (driversAgeCategory === 1 || driversAgeCategory === 3) ? driversAge : undefined,
      isPickUpAtAirport: pickUpItem.searchType === 'airport',
      pickUpSearchType: getCarHireSearchType(pickUpItem),
      dropOffSearchType: dropOffItem ? getCarHireSearchType(dropOffItem) : undefined,
      ...(bundledOrderId && { bundledOrderId }),
      ...(config.agentHub.isEnabled && { paymentSchedule: 'payFullyNow' }),
      ...(hasPickUpGeoLocation && { pickUpLatitude, pickUpLongitude }),
      ...(hasPickUpGeoLocation && hasDropOffGeoLocation && { dropOffLatitude, dropOffLongitude }),
    })
    const query = applyCarHireSearchRefinements(searchQuery, currentSearch)
    if (query !== searchQuery) {
      markCarHireSearchRefinementsApplied(query)
    }

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: mapGlobalSearchContextToSnowplowSearchEvent({
        searchItem: pickUpItem,
        secondarySearchItem: dropOffItem,
        searchVerticals: new Set(['car-hire']),
        pickUpTime,
        dropOffTime,
        driversAgeCategory,
        checkinDate: pickUpDate,
        checkoutDate: dropOffDate,
      }),
    }))

    dispatch(pushWithRegion('/search/car-hire', query))
  }

  const trySubmit = useCallback(() => {
    if (optimisticSubmits && formRef.current?.checkValidity()) {
      formRef.current.requestSubmit()
    }
  }, [optimisticSubmits])

  const setPickUpTimeDispatch = useCallback((time: string) => {
    if (formRef)
    { searchDispatch({ type: GlobalSearchStateActions.SET_PICKUP_TIME, pickupTime: time }) }
  }, [searchDispatch])

  const setReturnTimeDispatch = useCallback((time: string) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_RETURN_TIME, dropOffTime: time })
  }, [searchDispatch])

  const onSelectDriversAgeCategory = useCallback((nextCategory: App.CarHireDriverAgeCategory) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_DRIVERS_AGE_CATEGORY, driversAgeCategory: nextCategory })
  }, [searchDispatch])

  const onLocationChange = useCallback(() => {
    // Clear the geo location when the location changes
    searchDispatch({ type: GlobalSearchStateActions.SET_PICKUP_GEO_LOCATION, pickUpLatitude: undefined, pickUpLongitude: undefined })
    searchDispatch({ type: GlobalSearchStateActions.SET_DROP_OFF_GEO_LOCATION, dropOffLatitude: undefined, dropOffLongitude: undefined })
  }, [searchDispatch])

  const needsActualAge = driversAgeCategory === 1 || driversAgeCategory === 3

  const controlsContainerRef = useRef<HTMLDivElement>(null)
  const pickUpFieldRef = useRef<HTMLDivElement>(null)
  const dropOffFieldRef = useRef<HTMLDivElement>(null)

  const dateFieldsRefs = useMemo(() => [pickUpFieldRef, dropOffFieldRef], [])

  const pickupLocationLabel = useMemo(() => {
    return differentLocations
      ? intl.formatMessage({
          id: 'tiK709Pw8Yba_DB2W4Gj',
          defaultMessage: 'Pick-up location',
          description: 'Label for pick-up location input in car hire search',
        })
      : intl.formatMessage({
          id: 'hHHXnXZc_z8C6nulQlcb',
          defaultMessage: 'Pick-up & Drop-off location',
          description: 'Label for combined pick-up and drop-off location input in car hire search',
        })
  }, [differentLocations, intl])

  const formattedPickUpDate = pickUpDate ? dateFormatter(pickUpDate, INTL_SHORT_DAY_NAME_DAY_MONTH) : ''
  const formattedDropOffDate = dropOffDate ? dateFormatter(dropOffDate, INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR) : ''

  const dateRangeLabel = intl.formatMessage(
    {
      id: 'yobcRvxSMZboXjm6XZIB',
      defaultMessage: '{pickUpDate} → {dropOffDate}',
      description: 'Pick-up to drop-off date range in car hire search bar',
    },
    {
      pickUpDate: formattedPickUpDate,
      dropOffDate: formattedDropOffDate,
    },
  )

  const dateRangeWithDurationContent = <FormattedMessage
    id="u0wtzEwjxl81TotbHHQb"
    defaultMessage="<bold>{pickUpDate} → {dropOffDate}</bold> ({duration, plural, one {# day} other {# days}})"
    description="Date range with bold dates and rental duration in car hire search bar"
    values={{
      pickUpDate: formattedPickUpDate,
      dropOffDate: formattedDropOffDate,
      duration: duration ?? 0,
      bold: (chunks) => <b>{chunks}</b>,
    }}
  />

  const dateRangeOnlyContent = <b>{dateRangeLabel}</b>

  const dateRangeContent = duration ? dateRangeWithDurationContent : dateRangeOnlyContent

  return (
    <VerticalSpacer gap={20} as="form" ref={formRef} onSubmit={onSubmit} className={className}>
      <ControlsContainer ref={controlsContainerRef}>
        <Group direction="horizontal" gap={12} className="location-input">
          <SearchLocationInput
            label={pickupLocationLabel}
            placeholder={intl.formatMessage({
              id: 'uf0B6LiPQ4xedD7eaho4',
              defaultMessage: 'Search for an airport or city',
              description: 'Placeholder text for car hire location search input',
            })}
            searchTypes={CAR_HIRE_SEARCH_TYPES}
            variant="dropdown"
            dropdownSize={differentLocations ? 'M' : 'fill-anchor'}
            searchVertical="car_hire"
            required
            onChange={onLocationChange}
          />
          {differentLocations && <SearchLocationInput
            label={intl.formatMessage({
              id: 'MFqV0KUOWxSeYnI_qKx9',
              defaultMessage: 'Drop-off location',
              description: 'Label for drop-off location input in car hire search',
            })}
            placeholder={intl.formatMessage({
              id: 'uf0B6LiPQ4xedD7eaho4',
              defaultMessage: 'Search for an airport or city',
              description: 'Placeholder text for car hire location search input',
            })}
            searchTypes={CAR_HIRE_SEARCH_TYPES}
            isSecondaryLocation
            variant="dropdown"
            dropdownSize="M"
            searchVertical="car_hire"
            required
            onChange={onLocationChange}
          />}
        </Group>

        <CarHireDateTimeField
          className="pick-up-input"
          ref={pickUpFieldRef}
          fieldSearchMenu={SearchMenuStates.CarHirePickUpTime}
          date={pickUpDate}
          time={pickUpTime}
          emptyTimeLabel={intl.formatMessage({
            id: 'tPGZ3lKWrmw41WFzDhPb',
            defaultMessage: 'Start Time',
            description: 'Form field label for start time field in car hire search',
          })}
          inputLabel={intl.formatMessage({
            id: 'XyBhMoR6sZpBxGFa_GOS',
            defaultMessage: 'Pick-up date',
            description: 'Form field label for pick-up date input in car hire search',
          })}
          dateLabel={intl.formatMessage({
            id: '3Amj83V7xaEvIvJtJTi-',
            defaultMessage: 'Start date',
            description: 'Label for start date in car hire search in the search and discovery interface',
          })}
          setTime={setPickUpTimeDispatch}
        />

        <CarHireDateTimeField
          className="drop-off-input"
          ref={dropOffFieldRef}
          fieldSearchMenu={SearchMenuStates.CarHireReturnTime}
          date={dropOffDate}
          time={dropOffTime}
          emptyTimeLabel={intl.formatMessage({
            id: 'Mc2uO0XMu0t-kQoz4f_B',
            defaultMessage: 'End Time',
            description: 'Label for end time field in car hire search in the search interface',
          })}
          inputLabel={intl.formatMessage({
            id: 'CF4J6elvejHU0CPfWFec',
            defaultMessage: 'Drop-off date',
            description: 'Form field label for drop-off date input in car hire search',
          })}
          dateLabel={intl.formatMessage({
            id: 'MdhZrbhN_iHuydqwGxxh',
            defaultMessage: 'End date',
            description: 'Label for end date in car hire search in the search and discovery interface',
          })}
          setTime={setReturnTimeDispatch}
        />

        <SearchButton kind="primary" type="submit" size="large">
          {intl.formatMessage({
            id: '0dUrjXg6AUnjGyCH_bSY',
            defaultMessage: 'Search',
            description: 'Button text to search for car hire in the search and discovery interface',
          })}
        </SearchButton>

        <CarHireDatePicker
          anchorRef={controlsContainerRef}
          triggerRef={dateFieldsRefs}
          onApply={trySubmit}
        />
      </ControlsContainer>
      <Group direction="horizontal" horizontalAlign="space-between" gap={16} verticalAlign="center">
        <Group direction="horizontal" verticalAlign="center" gap={24}>
          <CheckboxInput
            defaultChecked={differentLocations}
            onChange={(e) => setDifferentLocations(e.currentTarget.checked)}
          >
            {intl.formatMessage({
              id: 'kL96yBTiddBx_dX93zMl',
              defaultMessage: 'Drop-off to a different location',
              description: 'Checkbox label for selecting different drop-off location in car hire search',
            })}
          </CheckboxInput>
          <CarHireSearchDriversAgeCategorySelect
            value={driversAgeCategory}
            onChange={onSelectDriversAgeCategory}
          />
          <DriversAgeContainer
            className={clsx({ hidden: !needsActualAge })}
            forwardedAs="label"
            direction="horizontal"
            verticalAlign="center"
            gap={8}
          >
            <Caption variant="large">
              {intl.formatMessage({
                id: 'pj9EIgLTkoLDPqLlTXsD',
                defaultMessage: 'How old is the driver?',
                description: 'Question label for driver age input in car hire search',
              })}
            </Caption>
            <DriversAgeInput />
          </DriversAgeContainer>
        </Group>
        {pickUpDate && dropOffDate && <BodyText variant="medium" colour="light-foreground-alt">
          {dateRangeContent}
        </BodyText>}
      </Group>
    </VerticalSpacer>
  )
}

const mapStateToProps = (state: App.State) => ({
  offerLists: state.carHire.offerLists,
})

export default connect(mapStateToProps)(CarHireSearchBar)
